import * as React from 'react';
import { Link } from 'react-router-dom';
import { MUIDataTableColumnDef } from 'mui-datatables';
import { formatMonitoringDeliveryDate, MonitoringPage } from './MonitoringPage';

export interface IOrderTotalMonitoringRow {
  orderId: number;
  orderNumber: string;
  parentOrderId?: number;
  status: string;
  lifetimeStatus: string;
  deliveryDate?: string | null;
  storedTotal: number;
  calculatorSubTotal: number;
  calculatorGst: number;
  calculatorFuelLevy: number;
  calculatorTotal: number;
  calculatorFamilySubTotal?: number;
  calculatorFamilyGst?: number;
  calculatorFamilyFuelLevy?: number;
  calculatorFamilyTotal?: number;
}

const money = (value?: number) => (value === undefined || value === null ? 'N/A' : value.toFixed(2));
const optionalNumber = (value?: number) => (value === undefined || value === null ? 'N/A' : value.toString());

const hiddenColumnOptions = {
  customBodyRender: money,
  display: 'false' as 'false',
  filter: false,
  sort: false,
};

const groupedColumnOptions = {
  download: false,
  filter: false,
  sort: false,
};

interface IOrderTotalsTableMeta {
  rowData: unknown;
}

interface ICostField {
  label: string;
  name: keyof IOrderTotalMonitoringRow;
}

const getRowValue = (
  tableMeta: IOrderTotalsTableMeta,
  name: keyof IOrderTotalMonitoringRow,
): number | undefined => {
  if (!Array.isArray(tableMeta.rowData)) return undefined;

  const index = orderTotalsColumns.findIndex((column) => typeof column !== 'string' && column.name === name);
  return index >= 0 ? tableMeta.rowData[index] as number | undefined : undefined;
};

const renderCostRow = (label: string, value?: number) => (
  <div className="monitoring-values-row" key={label}>
    <span className="monitoring-values-label">{label}</span>
    <span>{money(value)}</span>
  </div>
);

const renderCostSection = (
  title: string,
  tableMeta: IOrderTotalsTableMeta,
  fields: ICostField[],
) => (
  <div className="monitoring-values-section">
    <strong>{title}</strong>
    {fields.map((field) => renderCostRow(field.label, getRowValue(tableMeta, field.name)))}
  </div>
);

const renderCosts = (
  tableMeta: IOrderTotalsTableMeta,
  orderFields: ICostField[],
  familyFields: ICostField[],
) => (
  <div className="monitoring-values">
    {renderCostSection('Order', tableMeta, orderFields)}
    {renderCostSection('Family', tableMeta, familyFields)}
  </div>
);

const renderCalculatorCosts = (_value: unknown, tableMeta: IOrderTotalsTableMeta) => renderCosts(
  tableMeta,
  [
    { label: 'Subtotal', name: 'calculatorSubTotal' },
    { label: 'GST', name: 'calculatorGst' },
    { label: 'Fuel Levy', name: 'calculatorFuelLevy' },
    { label: 'Total', name: 'calculatorTotal' },
  ],
  [
    { label: 'Subtotal', name: 'calculatorFamilySubTotal' },
    { label: 'GST', name: 'calculatorFamilyGst' },
    { label: 'Fuel Levy', name: 'calculatorFamilyFuelLevy' },
    { label: 'Total', name: 'calculatorFamilyTotal' },
  ],
);

export const orderTotalsColumns: MUIDataTableColumnDef[] = [
  {
    name: 'orderNumber',
    label: 'Order',
    options: {
      customBodyRender: (value: string) => (
        <Link to={`/view-order/${value}`} target="_blank">
          {value}
        </Link>
      ),
    },
  },
  { name: 'deliveryDate', label: 'Delivery Date', options: { customBodyRender: formatMonitoringDeliveryDate } },
  { name: 'parentOrderId', label: 'Parent Order', options: { customBodyRender: optionalNumber } },
  { name: 'status', label: 'Status' },
  { name: 'lifetimeStatus', label: 'Lifetime Status' },
  { name: 'storedTotal', label: 'Stored Total', options: { customBodyRender: money } },
  {
    name: 'calculatorTotal',
    label: 'Calculator',
    options: { ...groupedColumnOptions, customBodyRender: renderCalculatorCosts },
  },
  { name: 'calculatorSubTotal', label: 'Calculator Subtotal', options: hiddenColumnOptions },
  { name: 'calculatorGst', label: 'Calculator GST', options: hiddenColumnOptions },
  { name: 'calculatorFuelLevy', label: 'Calculator Fuel Levy', options: hiddenColumnOptions },
  { name: 'calculatorFamilySubTotal', label: 'Calculator Family Subtotal', options: hiddenColumnOptions },
  { name: 'calculatorFamilyGst', label: 'Calculator Family GST', options: hiddenColumnOptions },
  { name: 'calculatorFamilyFuelLevy', label: 'Calculator Family Fuel Levy', options: hiddenColumnOptions },
  { name: 'calculatorFamilyTotal', label: 'Calculator Family Total', options: hiddenColumnOptions },
];

export const OrderTotalsMonitor = () => (
  <MonitoringPage
    apiPath="/api/monitoring/order-totals"
    allowTba
    columns={orderTotalsColumns}
    emptyMessage="No orders found for the selected criteria."
    title="Order Totals Monitor"
  />
);
